import React, { useState } from 'react';
import { Formik } from 'formik';
import { useI18n } from '@whizz/react-i18n';
import type { DateRangeType } from '../../forms/types';
import type { ToggleModalProps } from '../../components/modal/types';
import { DateRangePicker } from '../../components';
import CancelButtonModal from '../buttons/CancelButtonModal';
import ConfirmButtonModal from '../buttons/ConfirmButtonModal';
import { getDateStringWithLocale } from '../../util/dates';
import { EMPTY_VALUE, GLOBAL_ICONS } from '../../util/vars';
import { DATE_RANGE_SCHEMA } from '../../forms/validations';
import { DATEPICKER_RANGE } from '../../util/data-id';
import { ModalFooter } from '../../components/modal/Modal';
import classNames from 'classnames';

export interface DateRangeModalProps extends Pick<ToggleModalProps, 'toggle'> {
  initialValues: DateRangeType;
  onSubmit(values: DateRangeType): Promise<void>;
  closeCallback(): void;
  maxDate?: Date | null | undefined;
}

const DateRangeModal = ({ initialValues, onSubmit, closeCallback, toggle, maxDate }: DateRangeModalProps): JSX.Element => {
  const { translate, locale } = useI18n();
  const [ activeDatePicker, setActiveDatePicker ] = useState<'start' | 'end' | null>(null);
  const datePickerHandler = (type: Exclude<typeof activeDatePicker, null>) => () => setActiveDatePicker((previous) => previous === type ? null : type);

  const selectsStart = activeDatePicker === 'start';
  const selectsEnd = activeDatePicker === 'end';
  return (
    <React.Fragment>
      <Formik
        initialValues={
          {
            startDate: initialValues.startDate,
            endDate: initialValues.endDate,
          } as DateRangeType
        }
        validateOnMount
        validationSchema={DATE_RANGE_SCHEMA}
        onSubmit={(values) => {
          return Promise.resolve(onSubmit(values)).then(() => {
            toggle(false);
          });
        }}
      >
        {({
          handleSubmit,
          setFieldValue,
          values,
          isValid,
          isSubmitting,
          isValidating,
        }) => (
          <form onSubmit={handleSubmit}>
            <div className={'grid grid-cols-2 grid-rows-1 bg-light w-full mb-md'}>
              <button className={`flex-1 border p-sm text-left appearance-none ${classNames( { 'text-muted' : !selectsStart, 'font-bold': selectsStart })}`} type={'button'} onClick={datePickerHandler('start')}>
                <div className={'text-sm'}>
                  {translate('settings.DATE_RANGE.MODAL.start_date')}
                </div>
                <div className={`${classNames({ 'text-primary': selectsStart })}`}>
                  {values.startDate ? getDateStringWithLocale(values.startDate, locale?.getId()) : EMPTY_VALUE}
                </div>
              </button>
              <button className={`flex-1 border p-sm text-left appearance-none ${classNames( { 'text-muted' : !selectsEnd, 'font-bold': selectsEnd })}`} type={'button'} onClick={datePickerHandler('end')}>
                <div className={'text-sm'}>
                  {translate('settings.DATE_RANGE.MODAL.end_date')}
                </div>
                <div className={`${classNames({ 'text-primary': selectsEnd })}`}>
                  {values.endDate ? getDateStringWithLocale(values.endDate, locale?.getId()) : EMPTY_VALUE}
                </div>
              </button>
            </div>
            {activeDatePicker !== null ? (
              <DateRangePicker
                calendarClassName={DATEPICKER_RANGE}
                showMonthDropdown
                showYearDropdown
                scrollableYearDropdown
                hideInputs
                selected={selectsStart ? values.startDate : selectsEnd ? values.endDate : null}
                selectsStart={selectsStart}
                selectsEnd={selectsEnd}
                startDate={values.startDate}
                endDate={values.endDate}
                onChange={(date) => {
                  if (selectsStart) {
                    setFieldValue('startDate', date, true);
                  } else if (selectsEnd) {
                    setFieldValue('endDate', date, true);
                  }
                  setActiveDatePicker(null);
                }}
                minDate={selectsEnd ? values.startDate : null}
                maxDate={maxDate}
              />) : null }
            <ModalFooter>
              <CancelButtonModal
                type={'reset'}
                onClick={() => {
                  toggle(false);
                  closeCallback();
                }}
                disabled={isSubmitting || isValidating}
              />
              <ConfirmButtonModal
                type={'submit'}
                icon={GLOBAL_ICONS.calendar}
                disabled={!isValid || isSubmitting || isValidating}
              >
                {translate('settings.DATE_RANGE.MODAL.confirm')}
              </ConfirmButtonModal>
            </ModalFooter>
          </form>
        )}
      </Formik>
    </React.Fragment>
  );
};

DateRangeModal.displayName = 'DateRangeModal';

export default DateRangeModal;
